import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Download, FileText, Copy, CheckCircle2, Film } from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import { useDuplicateTrip } from '../trips/useTrips'
import { usePoints } from '../timeline/usePoints'
import { exportTripPdf, exportTripFilm } from '../../shared/api/export'
import { downloadBlob, slugify } from '../../shared/lib/download'
import { formatDateRange, tripDayCount } from '../../shared/lib/dates'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'

type TemplateId = 'classic' | 'modern' | 'minimal'

const TEMPLATES: { id: TemplateId; label: string; description: string; preview: string }[] = [
  {
    id: 'classic',
    label: 'Classic',
    description: 'Clean green accents, day sections with vertical bars. Great for screen viewing.',
    preview: '🌿',
  },
  {
    id: 'modern',
    label: 'Modern',
    description: 'Dark navy hero header, coloured day strips, emoji place markers. Bold and visual.',
    preview: '🌑',
  },
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'Greyscale only, ultra-compact. Optimised for black-and-white printing.',
    preview: '⬜',
  },
]

const FILM_STYLES: { id: TemplateId; label: string; description: string }[] = [
  { id: 'classic', label: 'Classic', description: 'Green title cards, warm captions on photos.' },
  { id: 'modern', label: 'Modern', description: 'Dark navy intro, indigo caption bars.' },
  { id: 'minimal', label: 'Minimal', description: 'Clean white slides, monochrome captions.' },
]

export function ExportTrip() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const navigate = useNavigate()
  const { data: trip, isLoading } = useTrip(id)
  const { data: points = [] } = usePoints(id)
  const duplicate = useDuplicateTrip()
  const [busy, setBusy] = useState(false)
  const [filmBusy, setFilmBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filmError, setFilmError] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>('classic')
  const [selectedFilmStyle, setSelectedFilmStyle] = useState<TemplateId>('classic')
  const [downloaded, setDownloaded] = useState<TemplateId | null>(null)
  const [filmDownloaded, setFilmDownloaded] = useState(false)

  async function handleDownload(template: TemplateId) {
    if (!trip) return
    setBusy(true)
    setError(null)
    try {
      const blob = await exportTripPdf(tripId, template)
      downloadBlob(blob, `${slugify(trip.title)}-${template}.pdf`)
      setDownloaded(template)
      setTimeout(() => setDownloaded(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleFilmDownload(style: TemplateId) {
    if (!trip) return
    setFilmBusy(true)
    setFilmError(null)
    try {
      const blob = await exportTripFilm(tripId, style)
      downloadBlob(blob, `${slugify(trip.title)}-${style}.mp4`)
      setFilmDownloaded(true)
      setTimeout(() => setFilmDownloaded(false), 3000)
    } catch (err) {
      setFilmError(err instanceof Error ? err.message : 'Film export failed')
    } finally {
      setFilmBusy(false)
    }
  }

  async function handleDuplicate() {
    if (!trip) return
    const copy = await duplicate.mutateAsync(trip.id)
    navigate(`/trips/${copy.id}`)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  if (!trip) {
    return <EmptyState title="Trip not found" description="It may have been deleted." />
  }

  const days = tripDayCount(trip.start_date, trip.end_date)

  return (
    <div className="mx-auto max-w-xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </Link>

      <div className="mb-5 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
          <FileText className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold text-st-text">Export & Share</h1>
          <p className="text-sm text-st-muted">PDF itineraries and travel films from your trip.</p>
        </div>
      </div>

      <div className="rounded-st-lg border border-st-line bg-st-surface p-5 mb-5">
        <h2 className="font-display text-lg font-semibold text-st-text">{trip.title}</h2>
        <p className="mt-1 text-sm text-st-muted">{formatDateRange(trip.start_date, trip.end_date)}</p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat label="Days" value={days} />
          <Stat label="Places" value={points.length} />
          <Stat label="Destination" text={trip.destination || '—'} />
        </div>
      </div>

      <div className="mb-5">
        <h3 className="text-sm font-semibold text-st-text mb-3">PDF template</h3>
        <div className="space-y-2">
          {TEMPLATES.map((tmpl) => (
            <label
              key={tmpl.id}
              className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition ${
                selectedTemplate === tmpl.id
                  ? 'border-st-primary bg-st-primary/5'
                  : 'border-st-line bg-st-surface hover:border-st-primary/40'
              }`}
            >
              <input
                type="radio"
                name="template"
                value={tmpl.id}
                checked={selectedTemplate === tmpl.id}
                onChange={() => setSelectedTemplate(tmpl.id)}
                className="mt-0.5 accent-st-primary"
              />
              <span className="text-2xl select-none">{tmpl.preview}</span>
              <div className="flex-1">
                <p className="font-semibold text-st-text">{tmpl.label}</p>
                <p className="text-xs text-st-muted mt-0.5">{tmpl.description}</p>
              </div>
              {downloaded === tmpl.id && (
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
              )}
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row mb-8">
        <Button
          variant="accent"
          onClick={() => void handleDownload(selectedTemplate)}
          disabled={busy}
        >
          {busy
            ? <Spinner label="Generating…" />
            : <><Download className="h-4 w-4" /> Download PDF — {TEMPLATES.find(t => t.id === selectedTemplate)?.label}</>}
        </Button>
        <Button
          variant="ghost"
          onClick={() => void handleDuplicate()}
          disabled={duplicate.isPending}
        >
          <Copy className="h-4 w-4" />
          {duplicate.isPending ? 'Duplicating…' : 'Use as template'}
        </Button>
      </div>

      {error && <p className="mb-6 text-sm text-st-danger">{error}</p>}

      <div className="rounded-st-lg border border-st-line bg-st-surface p-5">
        <div className="mb-4 flex items-center gap-2">
          <Film className="h-5 w-5 text-st-primary" />
          <div>
            <h3 className="font-display font-semibold text-st-text">Travel Film</h3>
            <p className="text-xs text-st-muted">
              Slideshow MP4 from your photos (story picks first) or a day-by-day route recap.
            </p>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {FILM_STYLES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelectedFilmStyle(s.id)}
              className={`rounded-st-full px-3 py-1.5 text-xs font-medium transition ${
                selectedFilmStyle === s.id
                  ? 'bg-st-primary text-white'
                  : 'bg-st-surface-2 text-st-muted hover:text-st-text'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <p className="mb-4 text-xs text-st-muted">
          {FILM_STYLES.find((s) => s.id === selectedFilmStyle)?.description}
        </p>

        <Button
          variant="outline"
          onClick={() => void handleFilmDownload(selectedFilmStyle)}
          disabled={filmBusy}
        >
          {filmBusy ? (
            <Spinner label="Rendering film…" />
          ) : (
            <>
              <Film className="h-4 w-4" />
              Download Film — {FILM_STYLES.find((s) => s.id === selectedFilmStyle)?.label}
              {filmDownloaded && <CheckCircle2 className="ml-1 h-4 w-4 text-green-500" />}
            </>
          )}
        </Button>
        {filmError && <p className="mt-3 text-sm text-st-danger">{filmError}</p>}
      </div>
    </div>
  )
}

function Stat({ label, value, text }: { label: string; value?: number; text?: string }) {
  return (
    <div className="rounded-st-md bg-st-surface-2 px-3 py-2 text-center">
      <p className="truncate text-lg font-semibold text-st-text">{text ?? value}</p>
      <p className="text-xs text-st-muted">{label}</p>
    </div>
  )
}
